import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { Copy } from "@phosphor-icons/react";
import {
  dismissInboxDuplicateThunk,
  restoreInboxDuplicateThunk,
  markInboxItemReviewed,
  selectInboxMutationsForDocument,
} from "../../../slice/InboxSlice";
import {
  DuplicateBanner,
  DuplicateBannerActions,
  DuplicateBannerBody,
  DuplicateBannerHeader,
  DuplicateBannerLabel,
  DuplicateBannerLink,
  DuplicateBannerOther,
} from "../InboxStyled";

/**
 * "You've probably already reviewed this one" for the selected inbox row.
 *
 * Matthew, 2026-08-05: a match against something nobody has opened yet
 * is not "a duplicate" — it's noise during triage. The backend
 * (`active_matches_for`) only ever hands this component a flag once
 * the matching copy has actually been marked reviewed by this same
 * user, so every label here can say so plainly rather than hedging.
 *
 * Lives in the right rail rather than on the list row: the row shows a
 * one-word chip, and the case for it — which document, why, and when
 * — belongs where there is space to make it. Triage lists should not
 * argue.
 *
 * Two actions, matching how the flag can be wrong or right:
 *
 *   Not a duplicate  — the detector was wrong. Clears the flag for the
 *                      whole team, since whether two documents are the
 *                      same letter is a fact about the documents.
 *   Mark reviewed    — the detector was right and the attorney has
 *                      already read this letter in its other copy, so
 *                      the row can be cleared without opening it.
 *
 * Nothing here blocks: an undismissed flag never prevents any action,
 * matching the advisory posture of the fee-notice duplicate warnings.
 */

const STRONG_KINDS = {
  identical: "Identical to one you already reviewed",
  contained_by: "Already included in one you reviewed",
  near_duplicate: "Likely already reviewed",
};

const formatReceived = (isoString) => {
  if (!isoString) return "";
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return "";
  return new Date(ts).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const InboxDuplicateBanner = ({ item, onDismissed }) => {
  const dispatch = useDispatch();
  const documentId = item?.id ?? null;
  const flag = item?.duplicate_flag || null;
  const dismissedMatchId = item?.dismissed_duplicate_match_id ?? null;

  const mutations = useSelector((state) =>
    selectInboxMutationsForDocument(state, documentId)
  );
  const busy =
    mutations?.duplicate === "loading" || mutations?.markReviewed === "loading";

  const other = flag?.other_document || null;
  const isStrong = flag?.strength === "strong";

  const label = useMemo(() => {
    if (!flag) return "";
    if (!isStrong) return "Possibly already reviewed";
    return STRONG_KINDS[flag.kind] || STRONG_KINDS.near_duplicate;
  }, [flag, isStrong]);

  const handleDismiss = useCallback(() => {
    if (!documentId || !flag) return;
    dispatch(
      dismissInboxDuplicateThunk({ documentId, matchId: flag.match_id })
    )
      .unwrap()
      .then(() => {
        message.success("Flag removed — these aren't the same document");
      })
      .catch((err) => {
        message.error(err?.message || "Couldn't dismiss the duplicate flag");
      });
  }, [dispatch, documentId, flag]);

  const handleRestore = useCallback(() => {
    if (!documentId) return;
    dispatch(
      restoreInboxDuplicateThunk({ documentId, matchId: dismissedMatchId })
    )
      .unwrap()
      .then(() => {
        message.success("Duplicate flag restored");
      })
      .catch((err) => {
        message.error(err?.message || "Couldn't restore the duplicate flag");
      });
  }, [dispatch, documentId, dismissedMatchId]);

  const handleMarkReviewed = useCallback(() => {
    if (!documentId) return;
    dispatch(markInboxItemReviewed({ documentId }))
      .unwrap()
      .then(() => {
        message.success("Marked reviewed");
        if (onDismissed) onDismissed(documentId);
      })
      .catch((err) => {
        message.error(err?.message || "Couldn't mark this reviewed");
      });
  }, [dispatch, documentId, onDismissed]);

  // Undo affordance after a dismissal. Dismissal is team-wide, so
  // without this one misclick would hide a real duplicate from
  // everyone with no way back.
  if (!flag && dismissedMatchId != null) {
    return (
      <DuplicateBanner $tone="dismissed" role="status">
        <DuplicateBannerHeader>
          <DuplicateBannerLabel $tone="dismissed">
            Duplicate flag dismissed
          </DuplicateBannerLabel>
          <DuplicateBannerLink
            type="button"
            onClick={handleRestore}
            disabled={busy}
          >
            Undo
          </DuplicateBannerLink>
        </DuplicateBannerHeader>
      </DuplicateBanner>
    );
  }

  if (!flag) return null;

  return (
    <DuplicateBanner $tone={isStrong ? "strong" : "weak"} role="status">
      <DuplicateBannerHeader>
        <DuplicateBannerLabel $tone={isStrong ? "strong" : "weak"}>
          <Copy size={13} weight="fill" aria-hidden="true" />
          {label}
        </DuplicateBannerLabel>
      </DuplicateBannerHeader>

      {other?.name ? (
        <DuplicateBannerOther>
          Matches <strong>{other.name}</strong>
          {other.received_at ? ` · received ${formatReceived(other.received_at)}` : ""}
          {other.is_read ? " · you marked that one reviewed" : ""}
        </DuplicateBannerOther>
      ) : null}

      {flag.reason ? (
        <DuplicateBannerBody>{flag.reason}</DuplicateBannerBody>
      ) : null}

      <DuplicateBannerActions>
        <DuplicateBannerLink
          type="button"
          onClick={handleDismiss}
          disabled={busy}
        >
          Not a duplicate
        </DuplicateBannerLink>
        {!item?.is_read && (
          <DuplicateBannerLink
            type="button"
            $primary
            onClick={handleMarkReviewed}
            disabled={busy}
          >
            Mark reviewed
          </DuplicateBannerLink>
        )}
      </DuplicateBannerActions>
    </DuplicateBanner>
  );
};

InboxDuplicateBanner.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_read: PropTypes.bool,
    dismissed_duplicate_match_id: PropTypes.number,
    duplicate_flag: PropTypes.shape({
      match_id: PropTypes.number,
      kind: PropTypes.string,
      strength: PropTypes.oneOf(["strong", "weak"]),
      reason: PropTypes.string,
      other_document: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        received_at: PropTypes.string,
        is_read: PropTypes.bool,
      }),
    }),
  }),
  onDismissed: PropTypes.func,
};

export default InboxDuplicateBanner;
